import { create } from 'zustand'
import type { AuthStatus, AuthUser } from './types'

type AuthState = {
  status: AuthStatus
  accessToken: string | null
  /** Absolute timestamp (ms) the access token expires at, derived from `expires_in`. */
  expiresAtMs: number | null
  user: AuthUser | null
  error: string | null

  setChecking: () => void
  setAuthenticated: (input: { accessToken: string; expiresIn: number; user: AuthUser }) => void
  setAnonymous: (error?: string | null) => void
  setError: (message: string | null) => void
  /** Convenience: replace the user object (e.g. after `/auth/me` rehydrate). */
  setUser: (user: AuthUser | null) => void
}

export const useAuthStore = create<AuthState>((set) => ({
  status: 'idle',
  accessToken: null,
  expiresAtMs: null,
  user: null,
  error: null,

  setChecking: () => set({ status: 'checking', error: null }),
  setAuthenticated: ({ accessToken, expiresIn, user }) =>
    set({
      status: 'authenticated',
      accessToken,
      expiresAtMs: Date.now() + expiresIn * 1000,
      user,
      error: null,
    }),
  setAnonymous: (error = null) =>
    set({
      status: 'anonymous',
      accessToken: null,
      expiresAtMs: null,
      user: null,
      error,
    }),
  setError: (message) => set({ error: message }),
  setUser: (user) => set({ user }),
}))

export function hasPermission(key: string): boolean {
  return useAuthStore.getState().user?.permissions?.includes(key) ?? false
}
